/**
 * useVideoCall — the presentation-layer brain for the 1:1 chat video-call page.
 *
 * Owns the full Agora orchestration the Flutter `VideoCallScreenState` owned
 * (`lib/presentation/screens/chat/video_call_screen.dart:111-493`), mapped onto
 * the `agora-rtc-react` declarative hooks:
 *   - role derivation + initial token fetch (`_init`, `:124-141`);
 *   - join + publish (`_initStreaming` / `_joinChannel`, `:201-217`, `:514-521`);
 *   - the five Firestore side-effect callbacks fired at the right lifecycle
 *     moments (`_onJoinChannelSuccess` / `_audienceUserDidNotJoinVideoCall` /
 *     `_onUserJoined` / `_onUserOffline` / `dispose`, `:312-345`, `:473-493`,
 *     `:143-163`);
 *   - the token-renewal lifecycle (`_onTokenPrivilegeWillExpire` / `_renewToken`
 *     / `_onTokenExpired` / `_onInvalidToken`, `:410-467`);
 *   - the per-message live subscription (`_subscribeToVideoCallMessage`,
 *     `:347-404`).
 *
 * `useBasePage` stays in the page (`presentation.md` — page-only). This hook is a
 * feature hook: it exposes a memoized `onPageDispose` cleanup callback the page
 * forwards into `useBasePage({ screenKey, onDispose })`, and the full view-model
 * the page + Task 14 (video views) + Task 15 (controls) render.
 *
 * The Flutter `latestLiveGiftInfo` branch (`:383-400`) — the transient "last
 * sent gift" overlay — IS ported here (the subscription sets `latestLiveGift` /
 * `latestLiveGiftOpacity`, exposed on `UseVideoCallResult` for the overlay to
 * render). The send-gift widget itself remains intentionally NOT ported.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import AgoraRTC, {
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient,
  useClientEvent,
} from 'agora-rtc-react';
import type {
  IAgoraRTCRemoteUser,
  ICameraVideoTrack,
  IMicrophoneAudioTrack,
} from 'agora-rtc-react';

import type { RootState } from '@/presentation/store';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { LatestLiveGiftInfoEntity } from '@/domain/entities/info/latestLiveGiftInfoEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { VideoCallRole } from '@/domain/enums/videoCall/videoCallRole';
import { generateLiveStreamTokenUseCase } from '@/domain/usecases/videoCall/generateLiveStreamTokenUseCase';
import { subscribeToVideoCallMessageUseCase } from '@/domain/usecases/videoCall/subscribeToVideoCallMessageUseCase';
import { addVideoCallMessageUseCase } from '@/domain/usecases/videoCall/addVideoCallMessageUseCase';
import { endVideoCallMessageUseCase } from '@/domain/usecases/videoCall/endVideoCallMessageUseCase';
import { audienceUserJoinedVideoCallUseCase } from '@/domain/usecases/videoCall/audienceUserJoinedVideoCallUseCase';
import { audienceUserDidNotJoinVideoCallUseCase } from '@/domain/usecases/videoCall/audienceUserDidNotJoinVideoCallUseCase';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { VIDEO_CALL_CONSTANTS } from '@/domain/constants/videoCallConstants';

const { VIDEO_CALL_WAIT_JOIN_DURATION_SECONDS } = VIDEO_CALL_CONSTANTS;

/**
 * Flutter joins with `uid: _liveStreamingUserId`, which is `0` until join
 * success (`video_call_screen.dart:76`, `:514-521`). Agora treats `uid: 0` as
 * "auto-assign"; the token is generated for the same `0` uid
 * (`_generateLiveStreamingToken` passes `_liveStreamingUserId`, `:523-530`), and
 * the real local uid is read off the connection afterwards (`:318`).
 */
const LOCAL_STREAMING_UID = 0;

export interface UseVideoCallArgs {
  /** The call initiator (Flutter `widget.callerUser`). */
  callerUser: UserEntity;
  /** The conversation peer / call recipient (Flutter `widget.audienceUser`). */
  audienceUser: UserEntity;
  /** The Agora channel id (`chatUsersInfo.groupChatId`, Flutter `widget.groupChatId`). */
  groupChatId: string;
  /** The call session id stored on the message as `videoCallId` (Flutter `widget.callMessageId`). */
  callMessageId: string;
  /**
   * The caller's local Agora streaming id, supplied to the audience so it can
   * render the caller's remote view (Flutter `widget.callerUserStreamingId`,
   * read at `:1023-1027`). `undefined` on the caller side.
   */
  callerUserStreamingId?: number;
  /** Chat-users context forwarded to the save path for the metadata doc. */
  chatUsersInfo: ChatUsersInfoEntity;
  /**
   * Pop callback supplied by the page. The hook never reaches into routing
   * itself (navigation is the page's concern, per the Clean Architecture flow);
   * it calls this on the terminal paths that used to call `Navigator.goBack()`
   * directly: token-regen failure (`renewToken` / `handleTokenDidExpire`), the
   * initial-token-fetch failure, and `endCall`. Should be stable (the page wraps
   * it in `useCallback`) so the hook's dep arrays do not churn.
   */
  goBack: () => void;
}

export interface UseVideoCallResult {
  // --- Role + lifecycle state (the page's state-view gates) ---
  /** `_isCaller` — `callerUser.firebaseId === currentUser.firebaseId` (`:113-115`). */
  isCaller: boolean;
  /** The remote (audience) streaming id once known (`_audienceLiveStreamingUserId`). */
  audienceStreamingId: number | null;
  /** The caller's streaming id (passed through for the audience's remote view). */
  callerUserStreamingId?: number;
  /** `_callEnded` — drives the "Call ended" state view (`:739-799`). */
  callEnded: boolean;
  /** `_audienceUserDidNotJoin` — drives the "X could not join" state view (`:801-861`). */
  audienceDidNotJoin: boolean;
  /** `_isRenewingStreamingToken` — drives the "regenerating token" overlay (`:720-737`). */
  isRenewingToken: boolean;
  /** True until the channel is open (`_isOpeningChannel` — the caller's "Calling X" loading gate, `:716`). */
  isOpeningChannel: boolean;

  // --- Agora media handles (Task 14 video views) ---
  /** The local camera track for the PiP view (Flutter local `AgoraVideoView`). */
  localCameraTrack: ICameraVideoTrack | null;
  /** The local microphone track (published; surfaced for the controls). */
  localMicrophoneTrack: IMicrophoneAudioTrack | null;
  /** The remote users in the channel (Task 14 selects the peer by uid). */
  remoteUsers: IAgoraRTCRemoteUser[];

  // --- Control actions (Task 15 buttons) ---
  /** `_isMute` (`muteLocalAudioStream`, `:1247-1278`). */
  isMute: boolean;
  /** `_isCameraOff` (`muteLocalVideoStream`, `:1280-1312`). */
  isCameraOff: boolean;
  /** Toggle mic mute via `localMicrophoneTrack.setMuted` (`:1249-1251`). */
  toggleMute: () => Promise<void>;
  /** Toggle camera via `localCameraTrack.setEnabled` (`:1282-1284`). */
  toggleCamera: () => Promise<void>;
  /**
   * Best-effort switch camera: cycles `setDevice` over the cameras enumerated
   * once on init (`:1313-1326`).
   */
  switchCamera: () => Promise<void>;
  /**
   * Number of available cameras, enumerated once by this hook. The controls
   * gate the switch-camera button on this (disabled when `< 2`) instead of
   * enumerating themselves.
   */
  cameraCount: number;
  /** End the call — leaves the channel and navigates back (Flutter `_onBackPress`, `:196-199`). */
  endCall: () => void;

  // --- Tap-to-clear-screen (Task 14) ---
  /** `_clearScreen` — hides the PiP + bottom controls (`:955`, `:1112-1118`). */
  clearScreen: boolean;
  /** Toggle `_clearScreen`. */
  toggleClearScreen: () => void;

  // --- Last-gift overlay state (the overlay reads these) ---
  /**
   * `_latestLiveGiftInfoValueNotifier` (`video_call_screen.dart:85-86`) — the
   * last gift surfaced during the call, or `null` when nothing is showing.
   */
  latestLiveGift: LatestLiveGiftInfoEntity | null;
  /**
   * `_latestLiveGiftOpacity` (`video_call_screen.dart:87`) — `1` while the gift
   * overlay is visible, `0` once faded out.
   */
  latestLiveGiftOpacity: number;

  // --- Lifecycle bridge (the page forwards into `useBasePage`) ---
  /**
   * `useBasePage.onInit` target — the web analog of Flutter `initState`/`_init`
   * (`:124-141`). Runs the genuine mount-once side-effects in one place
   * (symmetric to `onPageDispose`): the initial token fetch, the live
   * subscription setup, and the autoplay-fallback binding. Returns the init-side
   * teardown `useBasePage` runs on unmount. (Camera enumeration is NOT folded in
   * — it is keyed on the local camera track first existing, so it stays a
   * reactive effect.)
   */
  onPageInit: () => () => void;
  /**
   * `useBasePage.onDispose` target — the web analog of Flutter `dispose`
   * (`:143-163`): leave/release the channel (when not already ended), cancel the
   * subscription, and fire `EndVideoCallMessage` with the computed live duration.
   */
  onPageDispose: () => void;
}

export const useVideoCall = (args: UseVideoCallArgs): UseVideoCallResult => {
  const {
    callerUser,
    audienceUser,
    groupChatId,
    callMessageId,
    callerUserStreamingId,
    chatUsersInfo,
    goBack,
  } = args;

  const { l10n } = useLocale();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const client = useRTCClient();

  // Role: `_isCaller = callerUser.firebaseId == currentFirebaseUser.uid` (`:113-115`).
  const isCaller = useMemo(
    () => callerUser.firebaseId === currentUser?.firebaseId,
    [callerUser.firebaseId, currentUser?.firebaseId],
  );

  // --- React state (the view-model the page renders) ---
  const [appId, setAppId] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  /** Set once the initial token fetch fails — keeps `useJoin` disabled forever. */
  const [tokenFetchFailed, setTokenFetchFailed] = useState(false);
  const [audienceStreamingId, setAudienceStreamingId] = useState<number | null>(null);
  const [isRenewingToken, setIsRenewingToken] = useState(false);
  const [isOpeningChannel, setIsOpeningChannel] = useState(true);
  const [callEnded, setCallEnded] = useState(false);
  const [audienceDidNotJoin, setAudienceDidNotJoin] = useState(false);
  const [isMute, setIsMute] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [clearScreen, setClearScreen] = useState(false);
  /**
   * Number of available cameras, surfaced reactively so the controls re-render
   * once enumeration completes. Set alongside `camerasRef` from the single
   * one-shot `getCameras()` below; `VideoCallControls` consumes it via the page
   * (it no longer enumerates itself). A ref alone would not trigger the
   * control's re-render, so the count is mirrored into state here.
   */
  const [cameraCount, setCameraCount] = useState(0);
  /**
   * `_latestLiveGiftInfoValueNotifier` (`video_call_screen.dart:85-86`) — the
   * last gift surfaced during the call, or `null` when nothing is showing.
   * Exposed on `UseVideoCallResult` for the overlay to render.
   */
  const [latestLiveGift, setLatestLiveGift] =
    useState<LatestLiveGiftInfoEntity | null>(null);
  /**
   * `_latestLiveGiftOpacity` (`video_call_screen.dart:87`) — `1` while the gift
   * overlay is visible, `0` once faded out. Exposed on `UseVideoCallResult` for
   * the overlay to fade.
   */
  const [latestLiveGiftOpacity, setLatestLiveGiftOpacity] = useState(0);

  // --- Refs read from one-shot / cleanup contexts (always the latest value) ---
  /**
   * `_callStartedTimestamp` (`:80`). A ref because `dispose` (`onPageDispose`)
   * must read the LATEST value to compute `liveDuration` (`:154-158`), and it is
   * written from event handlers / the subscription that don't re-bind.
   */
  const callStartedTimestampRef = useRef<number | null>(null);
  /** Mirrors `audienceStreamingId` for the join-wait timeout's null check (`:326`). */
  const audienceStreamingIdRef = useRef<number | null>(null);
  /** Mirrors `callEnded` for `dispose`'s `if (!_callEnded)` guard (`:147`). */
  const callEndedRef = useRef(false);
  /** The latest call-message entity, fed to the End/Joined/DidNotJoin use cases. */
  const callMessageRef = useRef<ChatMessageEntity | null>(null);
  /** Guards `_onJoinChannelSuccess` so the caller fires `AddVideoCallMessage` once (`:312`). */
  const joinSuccessFiredRef = useRef(false);
  /**
   * Mirrors `useJoin`'s resolved local uid (`data: UID`) so the one-shot
   * `handleJoinSuccess` reads the LATEST value (the resolved uid may arrive on a
   * different tick than the `connection-state-change → CONNECTED` event). This is
   * the web analog of Flutter's `connection.localUid` (`video_call_screen.dart:318`)
   * and the authoritative `callerStreamingId` source (finding 4).
   */
  const joinedUidRef = useRef<number | null>(null);
  /**
   * Guards `_onUserJoined` so the joined write-back fires exactly once. The peer
   * can surface via EITHER the Agora `user-joined` event (peer joins after us)
   * OR the `useRemoteUsers()` reconciliation effect below (peer already present
   * when we join — the `user-joined` event does NOT replay for pre-existing
   * members). This ref prevents the two paths from double-firing.
   */
  const userJoinedFiredRef = useRef(false);
  /** Holds the join-wait timeout handle so cleanup can clear it (`:323-330`). */
  const joinWaitTimerRef = useRef<number | null>(null);
  /** The live-subscription `Unsubscribe` handle (`_streamSubscriptionBroadcasting`). */
  const unsubscribeRef = useRef<(() => void) | null>(null);
  /**
   * Holds the most-recently-armed last-gift fade timer so `onPageDispose` can
   * cancel a still-pending fade on unmount — the web analog of Flutter's
   * `if (mounted)` guard on the delayed callback (`video_call_screen.dart:395`).
   * It is deliberately NOT cleared or restarted when a new gift arrives: each
   * gift arms its own independent 5000ms fade and the earliest-scheduled timer
   * keeps running (parity with Flutter's uncancelled per-gift `Future.delayed`,
   * `:383-400`, and the `SendErrorBanner.tsx:27-32` first-timer-wins precedent).
   */
  const latestLiveGiftTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Whether the local tracks/channel have already been released (idempotent cleanup). */
  const releasedRef = useRef(false);
  /**
   * Latest local tracks, mirrored into refs so the stable `releaseStreaming`
   * (and the mount-only subscription / `onPageDispose`) always operate on the
   * CURRENT tracks rather than a stale closure captured at mount (when the
   * tracks are still `null`).
   */
  const localCameraTrackRef = useRef<ICameraVideoTrack | null>(null);
  const localMicrophoneTrackRef = useRef<IMicrophoneAudioTrack | null>(null);
  /**
   * The session's available cameras, enumerated ONCE after a local camera track
   * first exists (a track implies camera permission was granted, so the device
   * list is populated with labels). Camera enumeration is stable for the
   * session, so `switchCamera` reads from this cached list instead of calling
   * `AgoraRTC.getCameras()` on every tap.
   */
  const camerasRef = useRef<MediaDeviceInfo[]>([]);
  const camerasEnumeratedRef = useRef(false);

  // The caller's peer (audience) supplies `idTo` for `AddVideoCallMessage`
  // (`chat_screen.dart:794-807`). On the caller side this is `audienceUser`.
  const peerUser = audienceUser;

  // --- Agora declarative hooks ---
  // Join is gated on a successfully fetched token (Flutter aborts before
  // `_joinChannel` when the token is null, `:127-133`). `uid: 0` = auto-assign.
  const joinReady = appId != null && token != null && !tokenFetchFailed;
  // Bind the resolved local uid (`data: UID`) — the value `useJoin` resolves from
  // the underlying `client.join()` promise, the web analog of Flutter's
  // `connection.localUid` (`video_call_screen.dart:318`). Reading the real
  // server-assigned uid from here (rather than `client.uid` on the
  // `connection-state-change` event, which is `undefined` until join completes)
  // guarantees the caller writes the correct `callerStreamingId` the audience
  // matches against the caller's remote stream (finding 4).
  const { data: joinedUid } = useJoin(
    {
      appid: appId ?? '',
      channel: groupChatId,
      token: token ?? null,
      uid: LOCAL_STREAMING_UID,
    },
    joinReady,
  );

  // Local tracks are created once the join is armed (web is track-based — the
  // web analog of Flutter `enableVideo`/`enableAudio`, `:219-222`).
  const { localCameraTrack } = useLocalCameraTrack(joinReady);
  const { localMicrophoneTrack } = useLocalMicrophoneTrack(joinReady);

  // Publish both tracks once they exist and the join is armed (web analog of the
  // broadcaster publishing — Flutter publishes implicitly via the broadcaster
  // role, `:212`).
  usePublish([localMicrophoneTrack, localCameraTrack], joinReady);

  const remoteUsers = useRemoteUsers();

  // Both endpoints join as host/broadcaster (story `## Context`; Flutter
  // `setClientRole(clientRoleBroadcaster)` in `_initStreaming`, `:212`). A
  // `mode: "live"` client defaults to `audience` (cannot publish), so the role
  // MUST be promoted to `host` before publishing. Set it once the join is armed.
  const clientRoleSetRef = useRef(false);
  useEffect(() => {
    if (!joinReady || clientRoleSetRef.current) {
      return;
    }
    clientRoleSetRef.current = true;
    void client.setClientRole('host').catch((error: unknown) => {
      appLogger.error('setClientRole(host) failed', 'useVideoCall.setRole', {
        error: error as Error,
      });
    });
  }, [joinReady, client]);

  // Keep mirroring refs in sync with their state / the latest tracks.
  useEffect(() => {
    audienceStreamingIdRef.current = audienceStreamingId;
  }, [audienceStreamingId]);
  useEffect(() => {
    callEndedRef.current = callEnded;
  }, [callEnded]);
  useEffect(() => {
    localCameraTrackRef.current = localCameraTrack ?? null;
  }, [localCameraTrack]);
  // Enumerate the session's cameras ONCE, after the local camera track first
  // exists (the track implies camera permission was granted, so `getCameras()`
  // returns the full labelled device list). The list is stable for the session,
  // so `switchCamera` reads from this cache instead of re-enumerating per tap.
  useEffect(() => {
    if (localCameraTrack == null || camerasEnumeratedRef.current) {
      return;
    }
    camerasEnumeratedRef.current = true;
    void (async () => {
      try {
        const cameras = await AgoraRTC.getCameras();
        camerasRef.current = cameras;
        // Surface the count reactively so `VideoCallControls` (via the page)
        // re-renders once cameras are known — a ref write alone would not.
        setCameraCount(cameras.length);
      } catch (error) {
        appLogger.error('getCameras failed', 'useVideoCall.enumerateCameras', {
          error: error as Error,
        });
      }
    })();
  }, [localCameraTrack]);
  useEffect(() => {
    localMicrophoneTrackRef.current = localMicrophoneTrack ?? null;
  }, [localMicrophoneTrack]);
  useEffect(() => {
    // `data` is `UID = string | number`; the join requests numeric (`uid: 0`), so
    // coerce. `null` until `useJoin` resolves the join (its default seed is `0`).
    if (joinedUid == null) {
      joinedUidRef.current = null;
      return;
    }
    joinedUidRef.current =
      typeof joinedUid === 'number' ? joinedUid : Number(joinedUid);
  }, [joinedUid]);

  // ---------------------------------------------------------------------------
  // Token fetch (`_generateLiveStreamingToken`, `:523-530`) — returns the fresh
  // auth or `null`; `null`/missing `token` → toast + back (`:127-133`).
  // ---------------------------------------------------------------------------
  const fetchToken = useCallback(async (): Promise<string | null> => {
    try {
      const auth = await generateLiveStreamTokenUseCase.execute({
        channel: groupChatId,
        uid: LOCAL_STREAMING_UID,
        role: VideoCallRole.broadcaster,
      });
      if (auth == null || auth.token == null) {
        return null;
      }
      setAppId(auth.appId);
      return auth.token;
    } catch (error) {
      appLogger.error('generateLiveStreamToken failed', 'useVideoCall.fetchToken', {
        error: error as Error,
      });
      return null;
    }
  }, [groupChatId]);

  // ---------------------------------------------------------------------------
  // Streaming teardown (`_disposeStreaming`, `:165-168`). On the web this MUST
  // release the camera/mic (the browser does NOT auto-release): stop + close the
  // local tracks, then `client.leave()`. Idempotent — declared before the
  // orchestration callbacks that reference it (`handleAudienceDidNotJoin`,
  // `handleUserLeft`, the subscription, `onPageDispose`).
  // ---------------------------------------------------------------------------
  const releaseStreaming = useCallback(async () => {
    if (releasedRef.current) {
      return;
    }
    releasedRef.current = true;
    try {
      // Read the LATEST tracks off the refs (a stale closure captured at mount
      // would hold `null`). Stop releases the device; close frees it.
      localMicrophoneTrackRef.current?.stop();
      localMicrophoneTrackRef.current?.close();
      localCameraTrackRef.current?.stop();
      localCameraTrackRef.current?.close();
      await client.leave();
    } catch (error) {
      appLogger.error('releaseStreaming failed', 'useVideoCall.releaseStreaming', {
        error: error as Error,
      });
    }
  }, [client]);

  // --- The five orchestration side-effects -----------------------------------

  /**
   * `_audienceUserDidNotJoinVideoCall` (`:337-345`): tear down, fire the
   * DidNotJoin use case, set `_audienceUserDidNotJoin`/`_callEnded`.
   */
  const handleAudienceDidNotJoin = useCallback(() => {
    void releaseStreaming();
    // Fire-and-forget (Flutter `:337-345` does not await): the state updates
    // below run regardless of the use case's outcome, so keep the await inside a
    // self-contained IIFE rather than promoting this handler to async.
    void (async () => {
      try {
        await audienceUserDidNotJoinVideoCallUseCase.execute({
          callMessageId,
          message: callMessageRef.current,
          chatUsersInfo,
        });
      } catch (error) {
        appLogger.error(
          'audienceUserDidNotJoinVideoCall failed',
          'useVideoCall.handleAudienceDidNotJoin',
          { error: error as Error },
        );
      }
    })();
    setAudienceDidNotJoin(true);
    setCallEnded(true);
    callEndedRef.current = true;
  }, [callMessageId, chatUsersInfo, releaseStreaming]);

  /**
   * `_onJoinChannelSuccess` (`:312-335`): CALLER ONLY. Fire `onChannelOpened`
   * (= `AddVideoCallMessage`) with the resolved local uid, then arm the
   * `videoCallWaitJoinDurationSeconds` timeout — if still no audience, fire
   * "did not join".
   */
  const handleJoinSuccess = useCallback(() => {
    // Parity `:313-315` — only the caller drives the open/timeout flow. The
    // audience's `_isOpeningChannel` stays `true` here (Flutter early-returns
    // WITHOUT clearing it, `:313-315`); it is cleared in `handleUserJoined`
    // once the caller appears as a remote user (Flutter `_onUserJoined`, `:474`).
    if (!isCaller) {
      return;
    }
    if (joinSuccessFiredRef.current) {
      return;
    }
    joinSuccessFiredRef.current = true;

    // The caller's authoritative streaming id is the resolved join uid (the web
    // analog of Flutter's `connection.localUid`, `:318`) — NOT `client.uid` read
    // on the `connection-state-change` event, which is `undefined` until the join
    // completes and falls back to the `0` auto-assign sentinel the audience can
    // never match (finding 4). Fall back to `client.uid` only if it is itself a
    // real non-zero uid.
    const resolvedUid = joinedUidRef.current;
    const localUid =
      resolvedUid != null && resolvedUid !== 0
        ? resolvedUid
        : typeof client.uid === 'number' && client.uid !== 0
          ? client.uid
          : null;

    if (localUid == null) {
      // The resolved uid is not known yet — re-arm the one-shot guard and bail so
      // a later CONNECTED tick (once `useJoin` resolves the uid) re-attempts the
      // write, rather than persisting `callerStreamingId: 0`. Do NOT clear
      // `_isOpeningChannel` or arm the wait-join timeout on this branch (parity:
      // the open/timeout flow only proceeds once the channel is genuinely open
      // with a real uid).
      joinSuccessFiredRef.current = false;
      return;
    }

    setIsOpeningChannel(false);

    // Fire-and-forget (parity `:312-335`: the open flow arms the wait-join
    // timeout below WITHOUT awaiting the message write). Keep the await inside a
    // self-contained IIFE so the timer arming is not serialized behind it.
    void (async () => {
      try {
        const message = await addVideoCallMessageUseCase.execute({
          callerStreamingId: localUid,
          callMessageId,
          currentUser: callerUser,
          peerUser,
          chatUsersInfo,
        });
        // Optimistic resolution seed — the web analog of Flutter's
        // `_listItems.insert(0, chatMessage)` in `sendMessage`
        // (`chat_screen.dart:665-669`). The create-time `added` change is
        // dropped by the `modified`-only lifecycle path, so without this seed
        // the End/DidNotJoin write-backs would never have a message to resolve
        // (business-parity finding 1). Only seed if not already populated by the
        // subscription's `onLatest` (avoid clobbering a fresher snapshot).
        if (message != null && callMessageRef.current == null) {
          callMessageRef.current = message;
        }
      } catch (error) {
        appLogger.error('addVideoCallMessage failed', 'useVideoCall.handleJoinSuccess', {
          error: error as Error,
        });
      }
    })();

    // `Future.delayed(videoCallWaitJoinDurationSeconds)` → "did not join"
    // (`:323-330`). The `mounted && _audienceLiveStreamingUserId == null` guard
    // becomes the ref null-check (cleanup clears the timer on unmount).
    joinWaitTimerRef.current = window.setTimeout(() => {
      joinWaitTimerRef.current = null;
      if (audienceStreamingIdRef.current == null) {
        handleAudienceDidNotJoin();
      }
    }, VIDEO_CALL_WAIT_JOIN_DURATION_SECONDS * 1000);
  }, [isCaller, client, callMessageId, callerUser, peerUser, chatUsersInfo, handleAudienceDidNotJoin]);

  /**
   * `_onUserJoined` (`:473-485`): the audience appeared — record the streaming
   * id + start timestamp and fire `audienceUserJoinedVideoCall`.
   */
  const handleUserJoined = useCallback(
    (remoteUser: IAgoraRTCRemoteUser) => {
      // Fire the joined side-effects (and the write-back) exactly once whether
      // the peer arrived via the `user-joined` event or the `useRemoteUsers()`
      // reconciliation effect (already-present caller). Subsequent calls no-op.
      if (userJoinedFiredRef.current) {
        return;
      }
      userJoinedFiredRef.current = true;
      setIsOpeningChannel(false);
      const remoteUid = Number(remoteUser.uid);
      setAudienceStreamingId(remoteUid);
      audienceStreamingIdRef.current = remoteUid;
      callStartedTimestampRef.current = Date.now();

      // Fire-and-forget (Flutter `:473-485` does not await): the write-back is
      // the last side-effect here, but keep the await inside a self-contained
      // IIFE so this handler (invoked synchronously from the `user-joined` event
      // and the `useRemoteUsers()` reconciliation effect) stays non-async.
      void (async () => {
        try {
          await audienceUserJoinedVideoCallUseCase.execute({
            callMessageId,
            audienceStreamingId: remoteUid,
            message: callMessageRef.current,
            chatUsersInfo,
          });
        } catch (error) {
          appLogger.error(
            'audienceUserJoinedVideoCall failed',
            'useVideoCall.handleUserJoined',
            { error: error as Error },
          );
        }
      })();
    },
    [callMessageId, chatUsersInfo],
  );

  /**
   * `_onUserOffline` (`:487-493`): the remote left — tear down + `_callEnded`.
   */
  const handleUserLeft = useCallback(() => {
    void releaseStreaming();
    setCallEnded(true);
    callEndedRef.current = true;
  }, [releaseStreaming]);

  // ---------------------------------------------------------------------------
  // Reconcile the already-present peer (`useRemoteUsers()`). The Agora
  // `user-joined` event only fires for remote users who join AFTER the local
  // client subscribes — it does NOT replay for members already broadcasting at
  // join time. In the normal flow the caller initiates first and is already in
  // the channel by the time the audience joins, so the audience would never see
  // `user-joined` and would stay stuck on the "calling" loading view (and the
  // `audienceUserJoinedVideoCall` write-back would never fire). Already-present
  // members surface only via `useRemoteUsers()`, so detect the peer here and run
  // the same `_onUserJoined` flow (`video_call_screen.dart:473-485`). The
  // `userJoinedFiredRef` guard inside `handleUserJoined` keeps this and the
  // `user-joined` event from double-firing. Flutter does not need this because
  // `agora_rtc_engine`'s `onUserJoined` DOES fire for already-broadcasting peers.
  useEffect(() => {
    if (remoteUsers.length === 0 || userJoinedFiredRef.current) {
      return;
    }
    handleUserJoined(remoteUsers[0]);
  }, [remoteUsers, handleUserJoined]);

  // ---------------------------------------------------------------------------
  // Token renewal lifecycle.
  // ---------------------------------------------------------------------------

  /** `_renewToken` (`:451-467`): re-fetch; null → toast + back; else `renewToken`. */
  const renewToken = useCallback(async () => {
    const fresh = await fetchToken();
    if (fresh == null) {
      toast.error(l10n.chat.videoCallPage.tokenRegenFailedToast);
      goBack();
      return;
    }
    setToken(fresh);
    try {
      await client.renewToken(fresh);
    } catch (error) {
      appLogger.error('client.renewToken failed', 'useVideoCall.renewToken', {
        error: error as Error,
      });
    }
  }, [fetchToken, client, goBack, l10n.chat.videoCallPage.tokenRegenFailedToast]);

  /**
   * `_onTokenExpired` + `_onInvalidToken` (`:410-443`): set the renewing flag,
   * re-fetch; null → toast + back; else re-join, then clear the flag.
   *
   * Deviation note: Flutter splits this across three `connection-state-change`
   * reasons (`...TokenExpired` / `...RenewToken` / `...InvalidToken`) the web
   * SDK 4.x does not expose verbatim; the web SDK consolidates them into the
   * `token-privilege-did-expire` event (the connection is gone → a re-join is
   * required, mirroring Flutter's invalid-token `_joinChannel`, `:439`).
   */
  const handleTokenDidExpire = useCallback(async () => {
    setIsRenewingToken(true);
    const fresh = await fetchToken();
    if (fresh == null) {
      toast.error(l10n.chat.videoCallPage.tokenRegenFailedToast);
      goBack();
      return;
    }
    try {
      // Re-join with the fresh token (`:439` `_joinChannel`). `useJoin` owns the
      // connection lifecycle and is declarative, so swapping the token state
      // re-runs the join ONCE. Do NOT also call `client.join(...)` here: a manual
      // join on the client `useJoin` is already (re)connecting throws
      // `INVALID_OPERATION` ("already in connecting/connected state"), which lands
      // in the catch and logs on every token expiry. Flutter's `_onInvalidToken`
      // re-joins exactly once (`:439`); a single declarative re-join matches that.
      setToken(fresh);
    } catch (error) {
      appLogger.error('re-join after token expiry failed', 'useVideoCall.handleTokenDidExpire', {
        error: error as Error,
      });
    } finally {
      setIsRenewingToken(false);
    }
  }, [fetchToken, goBack, l10n.chat.videoCallPage.tokenRegenFailedToast]);

  // ---------------------------------------------------------------------------
  // Agora client events.
  // ---------------------------------------------------------------------------

  // Join success: web SDK reaches `CONNECTED` on `connection-state-change`. Fire
  // the one-shot caller open flow on the first transition into `CONNECTED`.
  useClientEvent(client, 'connection-state-change', (curState) => {
    if (curState === 'CONNECTED') {
      handleJoinSuccess();
    }
  });

  // The resolved local uid (`useJoin`'s `data`) can arrive on a LATER tick than
  // the `connection-state-change → CONNECTED` event. When the CONNECTED handler
  // ran before the uid resolved, `handleJoinSuccess` re-armed `joinSuccessFiredRef`
  // and bailed without writing the `0` sentinel — so re-attempt the one-shot open
  // flow here once the real uid lands (the internal `joinSuccessFiredRef` guard
  // makes this idempotent after the write succeeds). Finding 4.
  useEffect(() => {
    if (joinedUidRef.current != null && joinedUidRef.current !== 0) {
      handleJoinSuccess();
    }
  }, [joinedUid, handleJoinSuccess]);

  // `_onUserJoined` (`:280-282`, `:473-485`).
  useClientEvent(client, 'user-joined', (user) => {
    handleUserJoined(user);
  });

  // `_onUserOffline` (`:283-285`, `:487-493`).
  useClientEvent(client, 'user-left', () => {
    handleUserLeft();
  });

  // `_onTokenPrivilegeWillExpire` → `_renewToken` (`:271-273`, `:447-449`).
  useClientEvent(client, 'token-privilege-will-expire', () => {
    void renewToken();
  });

  // Token expired / invalid (`:257-266`, `:410-443`).
  useClientEvent(client, 'token-privilege-did-expire', () => {
    void handleTokenDidExpire();
  });

  // ---------------------------------------------------------------------------
  // Set up the live subscription (`_subscribeToVideoCallMessage`, `:347-404`).
  // Stable callback invoked once by `onPageInit` (page-driven `useBasePage.onInit`,
  // Finding 13) — NOT a mount effect. Stores the handle in `unsubscribeRef` for
  // both the init-side teardown and `onPageDispose`'s cancel (`:151-152`).
  // ---------------------------------------------------------------------------
  const setUpSubscription = useCallback(() => {
    const unsubscribe = subscribeToVideoCallMessageUseCase.execute({
      groupChatId,
      callMessageId,
      // Resolution source — fires for EVERY change type (`added`/`modified`/
      // `removed`), the web analog of Flutter's `_listItems`. Keeps the latest
      // entity for the End/Joined/DidNotJoin write-backs. The create-time
      // `added` change (Firestore delivers existing/just-created docs as
      // `added`) only reaches the ref through here — the `modified`-only
      // lifecycle path below would never see it (business-parity finding 1).
      onLatest: (message) => {
        callMessageRef.current = message;
      },
      onModified: (message) => {
        // Lifecycle reactions only — `modified`-only, parity `:361`.

        // `audienceStreamingId` set (`:364-369`): record it; the caller also
        // stamps `_callStartedTimestamp` ("if caller user closes the channel
        // first").
        if (message.audienceStreamingId != null) {
          setAudienceStreamingId(message.audienceStreamingId);
          audienceStreamingIdRef.current = message.audienceStreamingId;
          if (isCaller) {
            callStartedTimestampRef.current = Date.now();
          }
        }

        // Audience side: `isLive == false` → tear down + `_callEnded`
        // (`:375-381`).
        if (!isCaller && message.isLive === false) {
          setCallEnded(true);
          callEndedRef.current = true;
          void releaseStreaming();
        }

        // The last-gift overlay branch (`:383-400`). Gate parity: Flutter checks
        // `isLive != null && isLive! && latestLiveGiftInfo != null`; the web
        // entity types `isLive?: boolean`, so `=== true` treats `undefined`/
        // `false` as "not live". Effect order mirrors Flutter: opacity 1
        // (`:386`) → set the gift (`:387`, overwriting whatever gift was
        // showing) → after 5000ms fade it out with opacity 0 (`:393`) and null
        // the gift (`:394`).
        if (message.isLive === true && message.latestLiveGiftInfo != null) {
          setLatestLiveGiftOpacity(1);
          setLatestLiveGift(message.latestLiveGiftInfo);
          // Arm a NEW independent fade timer WITHOUT cancelling any earlier
          // still-pending one — the faithful port of Flutter's uncancelled
          // per-gift `Future.delayed` (`:388-399`): a rapid second gift's
          // window is truncated to the first gift's `+5s`, not granted a fresh
          // full 5s (cancel-then-restart would diverge from Flutter). Matches
          // the `SendErrorBanner.tsx:27-32` first-timer-wins convention. Because
          // a later `setTimeout` may leave the ref pointing at a newer timer
          // than the one that actually fires, the fade callback nulls the
          // gift/opacity unconditionally; the ref exists only so `onPageDispose`
          // can cancel the last-armed pending timer (Flutter's `if (mounted)`
          // guard, `:395`).
          latestLiveGiftTimeoutRef.current = setTimeout(() => {
            setLatestLiveGiftOpacity(0);
            setLatestLiveGift(null);
          }, ANIMATION_CONSTANTS.duration.autoHideDelayLong);
        }
      },
      onError: (error) => {
        appLogger.error(
          'subscribeToVideoCallMessage error',
          'useVideoCall.subscription',
          { error },
        );
      },
    });
    unsubscribeRef.current = unsubscribe;
    // `isCaller` is stable for the page's lifetime; `groupChatId`/`callMessageId`
    // come from the route args captured once at mount.
  }, [groupChatId, callMessageId, isCaller, releaseStreaming]);

  // ---------------------------------------------------------------------------
  // Control actions (Task 15).
  // ---------------------------------------------------------------------------
  const toggleMute = useCallback(async () => {
    if (localMicrophoneTrack == null) {
      return;
    }
    const next = !isMute;
    try {
      // Flutter `muteLocalAudioStream(!_isMute)` (`:1249-1251`).
      await localMicrophoneTrack.setMuted(next);
      setIsMute(next);
    } catch (error) {
      appLogger.error('toggleMute failed', 'useVideoCall.toggleMute', {
        error: error as Error,
      });
    }
  }, [localMicrophoneTrack, isMute]);

  const toggleCamera = useCallback(async () => {
    if (localCameraTrack == null) {
      return;
    }
    const nextOff = !isCameraOff;
    try {
      // Flutter `muteLocalVideoStream(!_isCameraOff)` (`:1282-1284`). Web:
      // `setEnabled(false)` when turning the camera OFF.
      await localCameraTrack.setEnabled(!nextOff);
      setIsCameraOff(nextOff);
    } catch (error) {
      appLogger.error('toggleCamera failed', 'useVideoCall.toggleCamera', {
        error: error as Error,
      });
    }
  }, [localCameraTrack, isCameraOff]);

  const switchCamera = useCallback(async () => {
    // TODO: @claude add a follow up task for this: switch-camera robustness /
    // facing-mode — Flutter video_call_screen.dart:1313-1326
    if (localCameraTrack == null) {
      return;
    }
    // Read from the list enumerated once on init rather than re-enumerating on
    // every tap (camera enumeration is stable for the session).
    const cameras = camerasRef.current;
    if (cameras.length < 2) {
      return;
    }
    try {
      const currentId = localCameraTrack.getTrackLabel();
      const currentIdx = cameras.findIndex((c) => c.label === currentId);
      const next = cameras[(currentIdx + 1) % cameras.length];
      await localCameraTrack.setDevice(next.deviceId);
    } catch (error) {
      appLogger.error('switchCamera failed', 'useVideoCall.switchCamera', {
        error: error as Error,
      });
    }
  }, [localCameraTrack]);

  const toggleClearScreen = useCallback(() => {
    setClearScreen((prev) => !prev);
  }, []);

  const endCall = useCallback(() => {
    // Flutter `_onBackPress` (`:196-199`) — pop the route; the dispose path
    // (`onPageDispose`) handles teardown + `EndVideoCallMessage`. The pop itself
    // is the page's concern, supplied via the injected `goBack` callback.
    goBack();
  }, [goBack]);

  // ---------------------------------------------------------------------------
  // Page-init (`_init`, `:124-141`). The page forwards this into
  // `useBasePage({ onInit })`, symmetric to `onPageDispose`. Runs the genuine
  // run-once-on-mount side-effects in one place (Finding 13): the initial token
  // fetch, the live subscription setup, and the autoplay-fallback binding. The
  // returned function is the init-side teardown `useBasePage` runs on unmount
  // (`onInit?.() → cleanup`, `useBasePage.ts:43`, `:272-289`).
  //
  // NOT folded in here: the camera enumeration (Finding 9). Camera enumeration is
  // keyed on the local camera track FIRST EXISTING (a track implies camera
  // permission was granted, so `getCameras()` returns the labelled device list);
  // the track is `null` at mount, so the enumeration is a reactive binding on
  // `localCameraTrack`, NOT a mount-once action — it stays an effect above. This
  // matches Finding 13's own constraint that "only the genuine mount-once
  // side-effects move into `onInit`" (and Finding 9's "after a local camera track
  // first exists" trigger).
  // ---------------------------------------------------------------------------
  const onPageInit = useCallback((): (() => void) => {
    // Initial token fetch (`:124-141`). `null`/missing token → toast + back, never
    // join (`:127-133`). The `cancelled` guard mirrors the old effect's
    // unmount-before-resolve protection.
    let cancelled = false;
    void (async () => {
      const fresh = await fetchToken();
      if (cancelled) {
        return;
      }
      if (fresh == null) {
        setTokenFetchFailed(true);
        toast.error(l10n.chat.videoCallPage.couldNotJoinToast);
        goBack();
        return;
      }
      setToken(fresh);
    })();

    // Live subscription (`_subscribeToVideoCallMessage`, `:347-404`). Stores its
    // handle in `unsubscribeRef`.
    setUpSubscription();

    // Autoplay fallback (task-prompt point 9). No blocking UI — the Mute button
    // surfaces audio state; best-effort, non-blocking. `remoteUsers` is read
    // inside the deferred callback, so binding once here is enough.
    AgoraRTC.onAutoplayFailed = () => {
      window.setTimeout(() => {
        remoteUsers.forEach((user) => {
          user.audioTrack?.play();
        });
      }, ANIMATION_CONSTANTS.duration.long);
    };

    // Init-side teardown (`useBasePage` runs this on unmount). Cancel the pending
    // token fetch, unbind the autoplay fallback, and cancel the subscription. The
    // subscription is ALSO cancelled in `onPageDispose` (which nulls
    // `unsubscribeRef.current` first), and `useBasePage` runs `onDispose` BEFORE
    // this init-cleanup (`useBasePage.ts:280-289`), so the `?.()` here is a safe
    // no-op on the normal path — no double-unsubscribe.
    return () => {
      cancelled = true;
      AgoraRTC.onAutoplayFailed = undefined;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
    };
    // Stable across the page's lifetime: `fetchToken`/`setUpSubscription`/`goBack`
    // are memoized; the l10n string + `remoteUsers` are read once at init.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchToken, setUpSubscription, goBack]);

  // ---------------------------------------------------------------------------
  // Page-dispose cleanup (`dispose`, `:143-163`). The page forwards this into
  // `useBasePage({ onDispose })`.
  // ---------------------------------------------------------------------------
  const onPageDispose = useCallback(() => {
    // Clear the pending join-wait timer.
    if (joinWaitTimerRef.current != null) {
      window.clearTimeout(joinWaitTimerRef.current);
      joinWaitTimerRef.current = null;
    }

    // Cancel any still-pending last-gift fade so it never fires after unmount —
    // the web equivalent of Flutter guarding its delayed `setState` with
    // `if (mounted)` (`:395`).
    if (latestLiveGiftTimeoutRef.current != null) {
      clearTimeout(latestLiveGiftTimeoutRef.current);
      latestLiveGiftTimeoutRef.current = null;
    }

    // `if (!_callEnded) _disposeStreaming()` (`:147-149`).
    if (!callEndedRef.current) {
      void releaseStreaming();
    }

    // Cancel the subscription (`:151-152`).
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;

    // `liveDuration = _callStartedTimestamp == null ? 0 : now - started`
    // (`:154-157`), then fire `onChannelClosed` (= `EndVideoCallMessage`,
    // `:158`). The use case's one-close guard handles the audience-side
    // double-fire (the `isLive=false` subscription echo).
    const startedAt = callStartedTimestampRef.current;
    const liveDurationMs = startedAt == null ? 0 : Date.now() - startedAt;
    // Fire-and-forget (Flutter `:158` fires `onChannelClosed` last and does not
    // await it). Keep the await inside a self-contained IIFE so `onPageDispose`
    // stays synchronous for `useBasePage`.
    void (async () => {
      try {
        await endVideoCallMessageUseCase.execute({
          callMessageId,
          liveDurationMs,
          message: callMessageRef.current,
          chatUsersInfo,
        });
      } catch (error) {
        appLogger.error('endVideoCallMessage failed', 'useVideoCall.onPageDispose', {
          error: error as Error,
        });
      }
    })();
  }, [releaseStreaming, callMessageId, chatUsersInfo]);

  return {
    isCaller,
    audienceStreamingId,
    callerUserStreamingId,
    callEnded,
    audienceDidNotJoin,
    isRenewingToken,
    isOpeningChannel,

    localCameraTrack: localCameraTrack ?? null,
    localMicrophoneTrack: localMicrophoneTrack ?? null,
    remoteUsers,

    isMute,
    isCameraOff,
    toggleMute,
    toggleCamera,
    switchCamera,
    cameraCount,
    endCall,

    clearScreen,
    toggleClearScreen,

    latestLiveGift,
    latestLiveGiftOpacity,

    onPageInit,
    onPageDispose,
  };
};
